import React from 'react';
import {Loader2, X} from 'lucide-react';
import {BiomarkerRule, BiomarkerSearchResult, RuleSource, RuleType} from '../types';
import {
    formatRuleTypeLabel,
    getRuleSourceOptionId,
    RULE_SOURCE_OPTIONS,
    requiresRangeInputs,
} from '../utils';
import type {UseBiomarkerSearch} from '../hooks/useBiomarkerSearch';

interface Props {
    rule: BiomarkerRule;
    index: number;
    removable: boolean;
    onChange: (patch: Partial<BiomarkerRule>) => void;
    onRemove: () => void;
    search: UseBiomarkerSearch;
}

const inputClass =
    'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary';

export const BiomarkerRuleRow: React.FC<Props> = ({rule, index, removable, onChange, onRemove, search}) => {
    const handleBiomarkerInput = (value: string) => {
        onChange({test_type: value, assay_code: null});
        search.search(value, index);
    };

    const handleSelectBiomarker = (result: BiomarkerSearchResult) => {
        onChange({test_type: result.name, assay_code: result.code ?? null});
        search.close();
    };

    const handleSourceChange = (optionId: string) => {
        const opt = RULE_SOURCE_OPTIONS.find((o) => o.id === optionId);
        if (!opt) return;
        // Switching source resets the previous source's payload so we
        // never submit a stale assay_code for an Age rule, etc.
        onChange({
            rule_source: opt.rule_source,
            measurement_key: opt.measurement_key,
            test_type: opt.test_type,
            assay_code: null,
        });
        search.close();
    };

    const sourceId = getRuleSourceOptionId(rule);
    const isBiomarkerRule = (rule.rule_source ?? RuleSource.BIOMARKER) === RuleSource.BIOMARKER;
    const isRange = requiresRangeInputs(rule.rule_type);
    const isAutocompleteOpen =
        isBiomarkerRule && search.activeIndex === index && (search.isSearching || search.searchResults.length > 0);

    return (
        <div className="relative rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5">
            {removable && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label={`Remove rule ${index + 1}`}
                    className="absolute right-1.5 top-1.5 rounded-md p-1 text-gray-400 hover:bg-mld-danger/5 hover:text-mld-danger">
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
            <div className="flex flex-wrap items-end gap-2 pr-6">
                <span className="w-12 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Rule {index + 1}
                </span>

                <div className="w-[150px]">
                    <label
                        htmlFor={`source-${index}`}
                        className="mb-0.5 block text-[11px] font-medium text-gray-700">
                        Source
                    </label>
                    <select
                        id={`source-${index}`}
                        value={sourceId}
                        onChange={(e) => handleSourceChange(e.target.value)}
                        className={inputClass}>
                        {RULE_SOURCE_OPTIONS.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {isBiomarkerRule ? (
                    <div className="relative min-w-[160px] flex-1">
                        <label
                            htmlFor={`biomarker-${index}`}
                            className="mb-0.5 block text-[11px] font-medium text-gray-700">
                            Biomarker
                        </label>
                        <input
                            type="text"
                            id={`biomarker-${index}`}
                            value={rule.test_type}
                            onChange={(e) => handleBiomarkerInput(e.target.value)}
                            onBlur={(e) => {
                                onChange({test_type: e.target.value.trim()});
                                setTimeout(() => {
                                    if (search.activeIndex === index) search.close();
                                }, 200);
                            }}
                            placeholder="e.g., LDL"
                            autoComplete="off"
                            className={inputClass}
                        />
                        {isAutocompleteOpen && (
                            <div
                                ref={search.dropdownRef}
                                className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg">
                                {search.isSearching ? (
                                    <div className="flex items-center px-3 py-2 text-gray-500">
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Searching...
                                    </div>
                                ) : search.searchResults.length === 0 ? (
                                    <div className="px-3 py-2 text-gray-400">No matches</div>
                                ) : (
                                    search.searchResults.map((result) => (
                                        <div
                                            key={result.id}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleSelectBiomarker(result);
                                            }}
                                            className="cursor-pointer px-3 py-1.5 hover:bg-mld-primary/10">
                                            {result.name}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="min-w-[160px] flex-1">
                        <label className="mb-0.5 block text-[11px] font-medium text-gray-700">
                            Measurement
                        </label>
                        <div className={`${inputClass} bg-gray-100 text-gray-600`}>{rule.test_type}</div>
                    </div>
                )}

                <div className="w-[140px]">
                    <label
                        htmlFor={`ruleType-${index}`}
                        className="mb-0.5 block text-[11px] font-medium text-gray-700">
                        Rule Type
                    </label>
                    <select
                        id={`ruleType-${index}`}
                        value={rule.rule_type}
                        onChange={(e) => onChange({rule_type: e.target.value as RuleType})}
                        className={inputClass}>
                        {Object.values(RuleType).map((rt) => (
                            <option key={rt} value={rt}>
                                {formatRuleTypeLabel(rt)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="w-[120px]">
                    <label
                        htmlFor={`gender-${index}`}
                        className="mb-0.5 block text-[11px] font-medium text-gray-700">
                        Gender
                    </label>
                    <select
                        id={`gender-${index}`}
                        value={rule.gender ?? ''}
                        onChange={(e) => onChange({gender: e.target.value || null})}
                        className={inputClass}>
                        <option value="">Any</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                </div>

                {isRange ? (
                    <div className="w-[150px]">
                        <label className="mb-0.5 block text-[11px] font-medium text-gray-700">Min / Max</label>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                aria-label={`Rule ${index + 1} minimum`}
                                value={rule.min_value ?? ''}
                                onChange={(e) =>
                                    onChange({min_value: e.target.value === '' ? null : parseFloat(e.target.value)})
                                }
                                step="0.01"
                                placeholder="Min"
                                className={`${inputClass} text-center font-mono`}
                            />
                            <span className="text-xs text-gray-400">–</span>
                            <input
                                type="number"
                                aria-label={`Rule ${index + 1} maximum`}
                                value={rule.max_value ?? ''}
                                onChange={(e) =>
                                    onChange({max_value: e.target.value === '' ? null : parseFloat(e.target.value)})
                                }
                                step="0.01"
                                placeholder="Max"
                                className={`${inputClass} text-center font-mono`}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="w-[100px]">
                        <label
                            htmlFor={`threshold-${index}`}
                            className="mb-0.5 block text-[11px] font-medium text-gray-700">
                            Value
                        </label>
                        <input
                            type="number"
                            id={`threshold-${index}`}
                            value={rule.threshold_value ?? ''}
                            onChange={(e) =>
                                onChange({threshold_value: e.target.value === '' ? null : parseFloat(e.target.value)})
                            }
                            step="0.01"
                            placeholder="Value"
                            className={`${inputClass} text-center font-mono`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
